import { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { StatusBar } from "./StatusBar";
import { LaraCopilotModal } from "./LaraCopilotModal.jsx";
import { LiveNotifications } from "./LiveNotifications.jsx";
import {
  LayoutDashboard, Radio, Activity, BarChart3, BookOpen, Crosshair,
  Settings, Cpu, LogOut, Menu, X, FileText, WifiOff
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/signals", label: "Signals", icon: Radio },
  { to: "/trades", label: "Trades", icon: BookOpen },
  { to: "/positions", label: "Positions", icon: Activity },
  { to: "/orders", label: "Orders", icon: FileText },
  { to: "/override", label: "Override", icon: Crosshair },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/strategies", label: "Strategies", icon: Cpu },
  { to: "/system", label: "System", icon: Cpu },
  { to: "/settings", label: "Settings", icon: Settings },
];

export default function AppLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [brokerStatus, setBrokerStatus] = useState("disconnected");

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/system/health`, { withCredentials: true, timeout: 15000 });
      setBrokerStatus(r.data.services?.broker || "disconnected");
    } catch (err) { void err; }
  }, []);

  const brokerConnected = brokerStatus === "connected";
  const brokerDegraded = brokerStatus === "degraded";

  useEffect(() => {
    fetchStatus();
    const iv = setInterval(fetchStatus, 5000);
    const handleAuthStateChanged = () => {
      fetchStatus();
    };
    window.addEventListener("auth-state-changed", handleAuthStateChanged);
    return () => {
      clearInterval(iv);
      window.removeEventListener("auth-state-changed", handleAuthStateChanged);
    };
  }, [fetchStatus]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 pb-8">
      {(brokerDegraded || !brokerConnected) && (
        <div data-testid="broker-disconnected-banner" className={`${brokerDegraded ? "bg-amber-900/60 border-amber-600" : "bg-amber-900/80 border-amber-700"} border-b px-4 py-1.5 text-center text-xs text-amber-200 font-semibold flex items-center justify-center gap-2`}>
          <WifiOff className="w-3 h-3" />
          {brokerDegraded
            ? "BROKER SYNC ISSUE — Session active but funds/data unavailable. Check Settings > Angel One."
            : "BROKER DISCONNECTED — Go to Settings > Angel One to connect"}
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-zinc-950/95 border-b border-zinc-800 backdrop-blur-sm">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-4 h-12">
          <div className="flex items-center gap-3">
            <button data-testid="mobile-menu-btn" className="lg:hidden text-zinc-400" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <span className="text-sm font-bold tracking-wider text-zinc-100">LARA BOT</span>
            <span className="text-[10px] text-zinc-500 hidden sm:inline">v4.1</span>
          </div>

          <div className="flex items-center gap-3">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${
              brokerConnected
                ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                : brokerDegraded
                  ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                  : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                brokerConnected ? "bg-emerald-400" : brokerDegraded ? "bg-amber-400 animate-pulse" : "bg-amber-400 animate-pulse"
              }`} />
              {brokerConnected ? "Broker OK" : brokerDegraded ? "Broker Sync" : "Broker Down"}
            </span>

            <button data-testid="logout-btn" onClick={handleLogout} className="text-zinc-500 hover:text-zinc-300 transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Nav — Desktop */}
        <nav className="hidden lg:flex max-w-[1600px] mx-auto px-4 gap-0.5 overflow-x-auto">
          {NAV.map(n => (
            <NavLink key={n.to} to={n.to}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t transition-colors
                 ${isActive ? "text-cyan-400 border-b-2 border-cyan-400 bg-zinc-900/50" : "text-zinc-400 hover:text-zinc-200"}`}>
              <n.icon className="w-3.5 h-3.5" />{n.label}
            </NavLink>
          ))}
        </nav>

        {/* Nav — Mobile */}
        {mobileOpen && (
          <nav className="lg:hidden bg-zinc-900/95 border-t border-zinc-800 px-2 py-2 grid grid-cols-5 gap-1 backdrop-blur-md">
            {NAV.map(n => (
              <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 py-2 text-[10px] rounded transition-colors
                   ${isActive ? "text-cyan-400 bg-zinc-800 font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>
                <n.icon className="w-4 h-4" />{n.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      {/* Main content */}
      <main className="max-w-[1600px] mx-auto px-2 sm:px-4 py-3 sm:py-4 pb-16 sm:pb-8">
        <Outlet context={{ brokerConnected }} />
      </main>

      {/* Status bar */}
      <StatusBar />

      {/* World Class AI Copilot & Live Activity Notifications */}
      <LaraCopilotModal />
      <LiveNotifications />
    </div>
  );
}
